import { useEffect, useState } from 'react';
import { fabric } from 'fabric';
import { useEditorHistory, useFabricEditor } from '../../lib/FabricJs';
import { useSelectionEdit } from '../Properties/useSelectionEdit';

// Players draw these text types; they do not draw 'textbox'.
const TEXT_TYPES = ['text', 'i-text'];

export const isTextObject = (object: fabric.Object): object is fabric.IText =>
  TEXT_TYPES.includes(object.type || '');

// The value every object has, or null when they differ.
export const sharedValue = <T>(
  objects: fabric.IText[],
  read: (object: fabric.IText) => T,
): T | null => {
  if (!objects.length) return null;
  const first = read(objects[0]);
  return objects.every((object) => read(object) === first) ? first : null;
};

// The selected text objects, read again when the selection changes and
// after every edit, undo or redo.
export const useSelectedText = () => {
  const { canvas } = useFabricEditor();
  const {
    history: { present },
  } = useEditorHistory();
  const editSelection = useSelectionEdit();
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!canvas) return;
    const refresh = () => setVersion((version) => version + 1);
    // Deferred: an undo/redo rebuilds the canvas in a later effect of the
    // same commit.
    queueMicrotask(refresh);
    const events = [
      'selection:created',
      'selection:updated',
      'selection:cleared',
    ];
    events.forEach((name) => canvas.on(name, refresh));
    return () => events.forEach((name) => canvas.off(name, refresh));
  }, [canvas, present]);

  const objects = (canvas?.getActiveObjects() || []).filter(isTextObject);

  // One undo step. Most text styles change the text's size, so a
  // multi-selection is rebuilt around the new bounds.
  const apply = (style: Partial<fabric.IText>) =>
    editSelection((selected) =>
      selected.filter(isTextObject).forEach((object) => object.set(style)),
    );

  // While a slider is dragged: shown on the slide, recorded by `apply` when
  // the drag ends.
  const preview = (style: Partial<fabric.IText>) => {
    objects.forEach((object) => object.set(style));
    canvas?.requestRenderAll();
  };

  return { objects, apply, preview };
};
